// ============================================================
// دستیار صوتی — ورودی
//
// طراحی عمداً «تک‌دستوری» است: کاربر دکمه را می‌زند، یک جمله
// می‌گوید، مرورگر نشست را می‌بندد، متن ارسال می‌شود و میکروفون
// بسته می‌ماند.
//
// حالت پیوسته ساخته شد و برداشته شد. سه دلیل: کروم حتی با
// continuous=true بعد از چند ثانیه سکوت نشست را می‌بندد؛ موتور
// بعد از چند نشست پیاپی گیر می‌کند (start موفق می‌شود ولی نه
// onstart می‌آید نه onend)؛ و صدای بلندگو وارد میکروفون می‌شود.
// پیش‌بینی‌پذیر بودن از هوشمندی مهم‌تر است.
//
// درس گران‌قیمت: وضعیت را حدس نزنید. runningRef فقط در onstart و
// onend مقدار می‌گیرد — چون onerror همیشه *قبل از* onend می‌آید و
// هر خوش‌بینی‌ای باعث می‌شود کد فکر کند میکروفون خاموش است در
// حالی که روشن است. نتیجه‌اش «می‌شنود ولی کاری نمی‌کند» بود.
// ============================================================

import { useCallback, useEffect, useRef, useState } from "react";

export const VOICE_BUILD = "havir-voice-1";

const START_GUARD_MS = 1500; // مهلت شکستن بن‌بست start بی‌پاسخ
const SESSION_CAP_MS = 12000; // سقف یک نشست شنیدن

interface Options {
    /** متن نهایی یک نشست — وقتی مرورگر نشست را بست */
    onResult: (text: string) => void;
    /** متن موقت، فقط برای نشان دادن در کادر ورودی */
    onInterim?: (text: string) => void;
    onError?: (message: string) => void;
}

const getRecognitionCtor = (): any => {
    if (typeof window === "undefined") return null;
    const w = window as any;
    return w.SpeechRecognition || w.webkitSpeechRecognition || null;
};

export const isVoiceSupported = (): boolean => !!getRecognitionCtor();

export const useVoiceInput = ({ onResult, onInterim, onError }: Options) => {
    const [listening, setListening] = useState(false);
    const [debug, setDebug] = useState("");

    const recognitionRef = useRef<any>(null);
    const runningRef = useRef(false);
    const startingRef = useRef(false);
    const startGuardRef = useRef<any>(null);
    const sessionCapRef = useRef<any>(null);
    const transcriptRef = useRef("");
    const buildRef = useRef<(() => any) | null>(null);

    // هندلرها در ref نگه داشته می‌شوند تا افکت mount یک بار اجرا شود
    // و با تغییر تابع والد، نمونه‌ی در حال شنیدن خراب نشود.
    const handlersRef = useRef<Options>({ onResult, onInterim, onError });
    handlersRef.current = { onResult, onInterim, onError };

    const clearTimers = () => {
        if (startGuardRef.current) {
            clearTimeout(startGuardRef.current);
            startGuardRef.current = null;
        }
        if (sessionCapRef.current) {
            clearTimeout(sessionCapRef.current);
            sessionCapRef.current = null;
        }
    };

    useEffect(() => {
        const SR = getRecognitionCtor();
        if (!SR) return;

        const attach = (instance: any) => {
            instance.lang = "fa-IR";
            instance.continuous = false;
            instance.interimResults = true;
            instance.maxAlternatives = 1;

            instance.onstart = () => {
                runningRef.current = true;
                startingRef.current = false;
                transcriptRef.current = "";
                setListening(true);
                setDebug("run:1 start:0 " + VOICE_BUILD);
            };

            instance.onresult = (event: any) => {
                let finalText = "";
                let interim = "";
                for (let i = event.resultIndex; i < event.results.length; i++) {
                    const result = event.results[i];
                    if (result.isFinal) finalText += result[0].transcript;
                    else interim += result[0].transcript;
                }
                if (finalText) transcriptRef.current += finalText;
                const preview = (transcriptRef.current + " " + interim).trim();
                if (handlersRef.current.onInterim) handlersRef.current.onInterim(preview);
            };

            instance.onerror = (event: any) => {
                const code = event?.error || "unknown";
                setDebug("err:" + code + " " + VOICE_BUILD);

                // aborted: خودمان قطع کرده‌ایم، خطا نیست.
                // no-speech: کاربر دکمه را زد و حرفی نزد — پیام قرمز لازم نیست.
                if (code === "aborted" || code === "no-speech") return;

                if (code === "not-allowed" || code === "service-not-allowed") {
                    if (handlersRef.current.onError)
                        handlersRef.current.onError("اجازه‌ی دسترسی به میکروفون داده نشده است.");
                    return;
                }
                if (handlersRef.current.onError)
                    handlersRef.current.onError("صدا گرفته نشد. دوباره امتحان کنید.");
            };

            instance.onend = () => {
                runningRef.current = false;
                startingRef.current = false;
                clearTimers();
                setListening(false);
                setDebug("run:0 start:0 " + VOICE_BUILD);

                const text = transcriptRef.current.trim();
                transcriptRef.current = "";
                if (text) handlersRef.current.onResult(text);
            };

            return instance;
        };

        // نمونه‌ی تازه در هر نشست: چون هیچ نمونه‌ای عمر طولانی ندارد،
        // حالت گیر کردن از ریشه حذف می‌شود.
        buildRef.current = () => {
            const previous = recognitionRef.current;
            if (previous) {
                // هندلرها را جدا می‌کنیم تا onend دیرهنگامِ نمونه‌ی مرده،
                // وضعیت نمونه‌ی تازه را خراب نکند.
                previous.onstart = null;
                previous.onresult = null;
                previous.onerror = null;
                previous.onend = null;
                try {
                    previous.abort();
                } catch {
                    /* نادیده */
                }
            }
            const fresh = attach(new SR());
            recognitionRef.current = fresh;
            runningRef.current = false;
            startingRef.current = false;
            return fresh;
        };

        // eslint-disable-next-line no-console
        console.log("[دستیار صوتی] " + VOICE_BUILD);

        return () => {
            clearTimers();
            const instance = recognitionRef.current;
            if (instance) {
                instance.onstart = null;
                instance.onresult = null;
                instance.onerror = null;
                instance.onend = null;
                try {
                    instance.abort();
                } catch {
                    /* نادیده */
                }
            }
        };
    }, []);

    const stop = useCallback(() => {
        clearTimers();
        const instance = recognitionRef.current;
        if (!instance) return;
        try {
            instance.stop();
        } catch {
            try {
                instance.abort();
            } catch {
                /* نادیده */
            }
        }
    }, []);

    const start = useCallback(() => {
        if (!buildRef.current) return;
        if (runningRef.current) return;

        const instance = buildRef.current();
        startingRef.current = true;
        transcriptRef.current = "";

        try {
            instance.start();
        } catch {
            startingRef.current = false;
            if (handlersRef.current.onError)
                handlersRef.current.onError("میکروفون باز نشد. یک بار دیگر بزنید.");
            return;
        }

        // بن‌بست: start() موفق شد ولی onstart نیامد. نمونه را رها
        // می‌کنیم تا فشار بعدی با نمونه‌ی تازه کار کند.
        startGuardRef.current = setTimeout(() => {
            if (!runningRef.current && startingRef.current) {
                startingRef.current = false;
                setListening(false);
                setDebug("start-stuck " + VOICE_BUILD);
                try {
                    instance.abort();
                } catch {
                    /* نادیده */
                }
            }
        }, START_GUARD_MS);

        // سقف نشست: اگر مرورگر خودش نبست، ما می‌بندیم.
        sessionCapRef.current = setTimeout(() => {
            if (runningRef.current) {
                try {
                    instance.stop();
                } catch {
                    /* نادیده */
                }
            }
        }, SESSION_CAP_MS);
    }, []);

    /**
     * فقط وقتی واقعاً در حال شنیدن است، فشار یعنی «بس کن».
     * اگر start زده شده ولی onstart نیامده، متوقف کردن کار را بدتر
     * می‌کند و کاربر باید دو بار بزند.
     */
    const toggle = useCallback(() => {
        if (listening) stop();
        else start();
    }, [listening, start, stop]);

    return {
        supported: isVoiceSupported(),
        listening,
        debug,
        start,
        stop,
        toggle,
    };
};
